import { cloudStorage } from './cloudAppwriteClient';
import { databases, account, ensureAuth } from './appwriteClient';
import { ID, Query } from 'appwrite';

const DATABASE_ID = '682acd26001f69683cd8'; // Default db database id
const COLLECTION_ID = 'admin-broadcast'; // 'admin' collection - broadcast collection

// ./cloudAppwriteClient.ts - cloudStorage
const BUCKET_ID = '6862996c001bd6436274'; // messaging file bucket

export interface FetchMessagesResult {
  documents: any[];
  lastId?: string;
  hasMore: boolean;
}

/**
 * Fetch admin broadcast messages with pagination support.
 * Returns documents ordered by $createdAt desc (newest first), plus lastId (id of last doc in page)
 * and hasMore indicating whether there may be more documents to fetch.
 *
 * Usage notes:
 * - To load the newest page: call without cursorAfter. The returned documents are newest->oldest.
 * - To load older pages: call with cursorAfter equal to the lastId returned from the previous call.
 */
export const fetchMessages = async (limit = 20, cursorAfter?: string): Promise<FetchMessagesResult> => {
  try {
    await ensureAuth();
    const queries: any[] = [Query.orderDesc('$createdAt'), Query.limit(limit)];
    if (cursorAfter) {
      queries.push(Query.cursorAfter(cursorAfter));
    }
    const response = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, queries);
    const docs = response.documents || [];
    const lastId = docs.length ? docs[docs.length - 1].$id : undefined;
    const hasMore = docs.length === limit;
    return { documents: docs, lastId, hasMore };
  } catch (error) {
    console.error('Error fetching messages from cloud Appwrite:', error);
    throw error;
  }
};

export const addMessage = async (message: string, img?: string) => {
  try {
    await ensureAuth();
    const data: { message: string; img?: string; time?: string } = { message, time: new Date().toISOString() };
    if (img) data.img = img;
    const response = await databases.createDocument(
      DATABASE_ID,
      COLLECTION_ID,
      ID.unique(),
      data
    );
    return response;
  } catch (error) {
    console.error('Error adding message to cloud Appwrite:', error);
    throw error;
  }
};

export const uploadChatImage = async (file: File): Promise<string> => {
  try {
    await ensureAuth();
    const fileId = ID.unique();
    const upload = await cloudStorage.createFile(BUCKET_ID, fileId, file);
    // Get the public URL for the uploaded file
    return cloudStorage.getFileView(BUCKET_ID, upload.$id).toString();
  } catch (error) {
    console.error('Error uploading chat image:', error);
    throw error;
  }
};

export const deleteMessage = async (messageId: string) => {
  try {
    await ensureAuth();
    await databases.deleteDocument(DATABASE_ID, COLLECTION_ID, messageId);
  } catch (error) {
    console.error('Error deleting message from cloud Appwrite:', error);
    throw error;
  }
}; 